import { assetService } from "../../../shared/services/AssetService";
import { AssetType } from "../../../shared/types/AssetType";
import { mathCalculationService } from "../../../shared/services/MathCalculationService";
import { OrderState } from "../../../shared/types/OrderState";
import { PortfolioGroupedItem } from "../models/PortfolioGroupedItem";
import { PositionModel } from "../../../features/portfolio-info/types/PositionModel";
import {
    portfolioCalculationService
} from "../../../shared/services/portfolio-calculation-service/PortfolioCalculationService";
import { PortfolioItemModel } from "../../../features/portfolio-info/types/PortfolioItemModel";
import { PortfolioModel } from "../../../features/portfolio-info/types/PortfolioModel";

class PositionsProcessor {
    getSummarizedPortfolioItems = (selectedPortfolio: PortfolioModel | null): PortfolioGroupedItem[] => {
        if (selectedPortfolio === null)
            return [];

        const getCurrentPositionBalance = (position: PositionModel): number =>
            assetService.isPositionOption(position) && assetService.isPositionOptionExpired(position)
                ? portfolioCalculationService.balance.calculatePositionBalanceAfterExpiration(position)
                : position.markPrice * position.size + position.volume;

        const mapPositions = (positions: PositionModel[]): PortfolioGroupedItem[] => {
            const result: PortfolioGroupedItem[] = [];

            selectedPortfolio.positions
                .map(position => {
                    const groupedItemModel: PortfolioGroupedItem = {
                        symbol: position.symbol,
                        type: position.type,
                        expireAt: position.expireAt,
                        expirationPrice: position.expirationPrice,
                        strike: position.strike,
                        previousSize: 0,
                        currentSize: position.size,
                        volume: position.volume,
                        processingItemOpenPrice: position.averageOpenPrice,
                        averageOpenPrice: position.averageOpenPrice,
                        markPrice: position.markPrice,
                        balance: getCurrentPositionBalance(position),
                        impliedVolatility: position.impliedVolatility,
                        customImpliedVolatility: position.customImpliedVolatility ?? position.impliedVolatility,
                        customImpliedVolatilityStep: position.customImpliedVolatilityStep,
                        delta: position.delta,
                        gamma: position.gamma,
                        vega: position.vega,
                        theta: position.theta,
                    };

                    result.push(groupedItemModel);
                });

            return result;
        }

        const result: PortfolioGroupedItem[] = mapPositions(selectedPortfolio.positions);

        const getCurrentOrderBalance = (order: PortfolioItemModel): number =>
            assetService.isOption(order) && assetService.isOptionExpired(order)
                ? portfolioCalculationService.balance.calculateOrderBalanceAfterExpiration(order)
                : (order.markPrice - order.openPrice) * order.size;

        const getVolume = (itemModel: PortfolioItemModel): number =>
            -(itemModel.size * itemModel.openPrice);

        const getAverageOpenPrice = (groupedItemModel: PortfolioGroupedItem): number | null => {
            if (assetService.isGroupedOptions(groupedItemModel) &&
                assetService.isGroupedOptionsExpired(groupedItemModel)
            ) {
                return null;
            }

            if (groupedItemModel.currentSize === 0)
                return null;

            if (groupedItemModel.previousSize === 0)
                return groupedItemModel.processingItemOpenPrice;

            const isPreviousPositionBullish = Math.sign(groupedItemModel.previousSize) === 1;
            const isCurrentPositionBullish = Math.sign(groupedItemModel.currentSize) === 1;

            if (isCurrentPositionBullish !== isPreviousPositionBullish)
                return groupedItemModel.processingItemOpenPrice;

            if (isCurrentPositionBullish === isPreviousPositionBullish) {
                const sizeChange = groupedItemModel.currentSize - groupedItemModel.previousSize;

                if (isCurrentPositionBullish && sizeChange < 0 || !isCurrentPositionBullish && sizeChange > 0)
                    return groupedItemModel.averageOpenPrice;

                const groupPositionWeightedPrice = groupedItemModel.averageOpenPrice! * groupedItemModel.previousSize;
                const processingPositionWeightedPrice = groupedItemModel.processingItemOpenPrice * sizeChange;
                const totalSize = groupedItemModel.previousSize + sizeChange;

                return (groupPositionWeightedPrice + processingPositionWeightedPrice) / totalSize;
            }

            throw new Error("Unhandled case during processing getAverageOpenPrice()");
        }

        selectedPortfolio.portfolioItems
            .filter(order => order.state == OrderState.Active)
            .sort((x, y) => x.openedPositionAt > y.openedPositionAt ? 1 : -1)
            .map(order => {
                let groupedItemModel = result.find(x => x.symbol === order.symbol && x.type === order.type);
                
                if (groupedItemModel) {
                    groupedItemModel.previousSize = mathCalculationService.round(groupedItemModel.currentSize, 6);
                    groupedItemModel.processingItemOpenPrice = order.openPrice;

                    groupedItemModel.volume += getVolume(order);
                    groupedItemModel.balance += getCurrentOrderBalance(order);

                    if (!(assetService.isOption(order) && assetService.isOptionExpired(order))) {
                        groupedItemModel.currentSize = mathCalculationService
                            .round(groupedItemModel.currentSize + order.size, 6);

                        groupedItemModel.delta += order.delta;

                        if (groupedItemModel.gamma !== null)
                            groupedItemModel.gamma += order.gamma ?? 0;

                        if (groupedItemModel.vega !== null)
                            groupedItemModel.vega += order.vega ?? 0;

                        if (groupedItemModel.theta !== null)
                            groupedItemModel.theta += order.theta ?? 0;
                    }

                    groupedItemModel.averageOpenPrice = getAverageOpenPrice(groupedItemModel);
                } else {
                    groupedItemModel =
                        {
                            symbol: order.symbol,
                            type: order.type,
                            expireAt: order.expireAt,
                            expirationPrice: order.expirationPrice,
                            strike: order.strike,
                            previousSize: 0,
                            currentSize: mathCalculationService.round(order.size, 6),
                            volume: getVolume(order),
                            processingItemOpenPrice: order.openPrice,
                            averageOpenPrice: order.openPrice,
                            markPrice: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? 0
                                : order.markPrice,
                            balance: getCurrentOrderBalance(order),
                            impliedVolatility: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.markImpliedVolatility,
                            customImpliedVolatility: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.customImpliedVolatility ?? order.markImpliedVolatility,
                            customImpliedVolatilityStep: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.customImpliedVolatilityStep,
                            delta: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? 0
                                : order.delta,
                            gamma: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.gamma,
                            vega: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.vega,
                            theta: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.theta,
                        };

                    result.push(groupedItemModel);
                }
            });

        result
            .sort((x, y) => {
                if ((x.type === AssetType.Futures ||
                        x.type === AssetType.Spot) &&
                    (y.type === AssetType.Call ||
                        y.type === AssetType.Put))
                {
                    return -1;
                }

                if (x.type === AssetType.Spot && y.type === AssetType.Futures)
                {
                    return -1;
                }

                if (x.type === AssetType.Call && y.type === AssetType.Put)
                {
                    return -1;
                }

                return 1;
            })
            .sort((x, y) => {
                if (!x.expireAt || !y.expireAt)
                {
                    return 0;
                }

                if (x.type !== AssetType.Call && y.type !== AssetType.Put)
                {
                    return 0;
                }

                return x.expireAt > y.expireAt ? 1 : -1;
            })
            .sort((x, y) => {
                if (!x.strike || !y.strike)
                {
                    return 0;
                }

                if (x.type !== AssetType.Call && y.type !== AssetType.Put)
                {
                    return 0;
                }

                return x.strike > y.strike ? 1 : -1;
            });

        return result;
    }
}

export const positionsProcessor = new PositionsProcessor();
